import React from 'react'
import { Table, Button, Tag, Space } from 'antd'
import { EyeOutlined, UndoOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Payment } from '../types/payment.types'
import { formatINR } from '../../../shared/utils/formatters'

interface PaymentsTableProps {
    payments: Payment[]
    onRowClick: (payment: Payment) => void
    onRefundClick: (payment: Payment) => void
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({
    payments,
    onRowClick,
    onRefundClick
}) => {
    const columns: ColumnsType<Payment> = [
        {
            title: 'Payment ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <strong>{id}</strong>,
        },
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            key: 'orderId',
        },
        {
            title: 'Customer',
            dataIndex: 'customer',
            key: 'customer',
        },
        {
            title: 'Merchant',
            dataIndex: 'merchant',
            key: 'merchant',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => formatINR(amount),
        },
        {
            title: 'Method',
            dataIndex: 'method',
            key: 'method',
            render: (method: string) => <Tag>{method.toUpperCase()}</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    pending: 'orange',
                    success: 'green',
                    failed: 'red',
                    refunded: 'gray',
                }
                return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>
            },
        },
        {
            title: 'Transaction ID',
            dataIndex: 'transactionId',
            key: 'transactionId',
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => onRowClick(record)}
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        icon={<UndoOutlined />}
                        danger
                        disabled={record.status !== 'success'}
                        onClick={(e) => {
                            e.stopPropagation()
                            onRefundClick(record)
                        }}
                    >
                        Refund
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={payments}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: payments.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} payments`,
            }}
        />
    )
}
